/* eslint-disable react/no-array-index-key */
import { Button, ComponentType, LoadingButton, Grid, IconName, Icon, Text } from '@gcl/react-core';
import { ITagViewItem, TagView } from '@masterSchedule/detail/tagView/TagView';
import * as React from 'react';
import { EDetailPageTopBarItemType } from './detailPageTopBar.model';
import './DetailPageTopBar.scss';
import { useProjectIsArchived } from '@config/config.hooks';

export interface IDetailPageTopBarItem {
  type: EDetailPageTopBarItemType;
  componentType?: ComponentType;
  title?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | React.MouseEventHandler<SVGElement> | undefined;
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  showNoneInReadOnly?: boolean;
  showNoneInArchivedProject?: boolean;
  disable?: boolean;
  buttonIconName?: IconName;
  error?: Error | null | undefined;
  tags?: ITagViewItem[] | null | undefined;
  onAddTag?: (newTag: ITagViewItem) => void;
  onRemoveTag?: (index: number) => void;
}

interface IDetailPageTopBarProps {
  leftItems?: (IDetailPageTopBarItem | React.ReactElement)[];
  rightItems?: (IDetailPageTopBarItem | React.ReactElement)[];
  isReadOnly?: boolean;
  className?: string;
}

export const DetailPageTopBar = ({
  leftItems = [],
  rightItems = [],
  isReadOnly = false,
  className,
}: IDetailPageTopBarProps) => {
  const isProjectArchived = useProjectIsArchived();

  const detailPageTopBarItem = ({
    type,
    componentType,
    title,
    buttonIconName,
    isError = false,
    isLoading = false,
    isSuccess = false,
    showNoneInReadOnly,
    showNoneInArchivedProject = true,
    onClick,
    disable,
    tags,
    error,
    onRemoveTag,
    onAddTag,
  }: IDetailPageTopBarItem) => {
    if (showNoneInReadOnly && isReadOnly) {
      return undefined;
    }
    if (isProjectArchived && showNoneInArchivedProject) return undefined;

    const key = `${title}_${type}`;
    switch (type) {
      case EDetailPageTopBarItemType.button:
        return (
          <Button
            type={componentType}
            prefixIconName={buttonIconName || undefined}
            onClick={onClick ? (onClick as React.MouseEventHandler<HTMLButtonElement>) : undefined}
            disabled={disable}
            key={key}
            className="gcl-gems-detail-page-top-bar-btn"
          >
            {title}
          </Button>
        );
      case EDetailPageTopBarItemType.iconButton:
        return buttonIconName ? (
          <Icon
            iconName={buttonIconName}
            size={20}
            onClick={onClick ? (onClick as React.MouseEventHandler<SVGElement>) : undefined}
            key={key}
            className="gcl-ic-btn-md"
          />
        ) : undefined;

      case EDetailPageTopBarItemType.loadingButton:
        return (
          <Grid
            item
            component={LoadingButton}
            prefixIconName={buttonIconName || undefined}
            disabled={disable}
            type={componentType}
            onClick={onClick ? (onClick as React.MouseEventHandler<HTMLButtonElement>) : undefined}
            isLoading={isLoading}
            isError={isError}
            isSuccess={isSuccess}
            timeToResetSuccessState={3000}
            timeToResetErrorState={5000}
            key={key}
          >
            <span>{title}</span>
          </Grid>
        );

      case EDetailPageTopBarItemType.tagView:
        return (
          <TagView
            onRemove={onRemoveTag}
            onAdd={onAddTag}
            isLoading={isLoading}
            tags={tags || []}
            error={error}
            disabled={disable}
          />
        );

      case EDetailPageTopBarItemType.text:
        return (
          <Text type={componentType} className="gcl-ft-lg">
            {title}
          </Text>
        );

      default:
        return undefined;
    }
  };

  return (
    <>
      <Grid
        container
        className={`${className} gcl-mb-md gcl-gems-detail-page-top-bar-container`}
        flexWrap="nowrap"
        justifyContent="space-between"
      >
        <Grid container item flex="1 1 auto" className="gcl-gems-detail-page-top-bar left">
          {leftItems.map((item, index) => (
            <React.Fragment key={`leftitem-${index}`}>
              {React.isValidElement(item) ? <>{item}</> : detailPageTopBarItem(item as IDetailPageTopBarItem)}
            </React.Fragment>
          ))}
        </Grid>
        <Grid
          container
          item
          flex="0 1 fit-content"
          flexWrap="nowrap"
          alignItems="center"
          columnSpacing={1}
          className="gcl-gems-detail-page-top-bar right"
        >
          {rightItems.map((item, index) => (
            <Grid item key={`gcl-gems-detail-page-top-bar-right-${index.toString()}`}>
              <React.Fragment key={`rightitem-${index}`}>
                {React.isValidElement(item) ? <>{item}</> : detailPageTopBarItem(item as IDetailPageTopBarItem)}
              </React.Fragment>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </>
  );
};
